import { ClipboardCheck, CheckCircle, GraduationCap, FileText, CreditCard } from "lucide-react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { formatDateToLocale } from "@utils/formatting";
import { getPhaseFromStatus } from "../utils/get-phase-from-status";
import type { Phase } from "../utils/get-phase-from-status";
import { EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS } from "@academy-context/shared/domain/types/enums/external-provider-batch-request";

interface Step {
  label: string;
  icon: typeof ClipboardCheck;
  phase: Phase;
}

const getSteps = (currentStatus: EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS): Step[] => [
  { label: "Vérification", icon: ClipboardCheck, phase: "review" },
  { label: "Validation", icon: CheckCircle, phase: "pending_validation" },
  {
    label: currentStatus === EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.SCHEDULED ? "Planifié" : "Formation",
    icon: GraduationCap,
    phase: "training_in_progress",
  },
  { label: "Facturation", icon: FileText, phase: "invoice_editable" },
  { label: "Paiement", icon: CreditCard, phase: "invoice_readonly" },
];

const PHASE_TO_STEP_INDEX: Record<Phase, number> = {
  review: 0,
  pending_validation: 1,
  training_in_progress: 2,
  invoice_editable: 3,
  invoice_readonly: 4,
};

interface ProgressStepperProps {
  currentStatus: EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS;
  trainingEndDate: string;
}

export const ProgressStepper = ({ currentStatus, trainingEndDate }: ProgressStepperProps) => {
  const currentPhase = getPhaseFromStatus(currentStatus);
  const currentIndex = PHASE_TO_STEP_INDEX[currentPhase];
  const steps = getSteps(currentStatus);

  const stepCount = steps.length;

  return (
    <div className="mx-4">
      <div className="relative flex justify-between">
        {/* Background connector line (full width between first and last icon centers) */}
        <div className="absolute inset-x-5 top-5 h-0.5 -translate-y-1/2 bg-muted-foreground/20" />
        {/* Green completed connector overlay */}
        {currentIndex > 0 && (
          <div
            className="absolute left-5 top-5 h-0.5 -translate-y-1/2 bg-green-500"
            style={{ width: `calc((100% - 2.5rem) * ${(currentIndex - 1) / (stepCount - 1)})` }}
          />
        )}
        {/* Step icons + labels */}
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const Icon = step.icon;

          return (
            <div key={step.phase} className="flex flex-col items-center gap-1.5">
              <div
                className={cn(
                  "relative z-10 flex size-10 items-center justify-center rounded-full border-2",
                  isCompleted && "border-green-500 bg-green-500 text-white",
                  isCurrent && "border-blue-500 bg-slate-900 text-blue-500",
                  !isCompleted && !isCurrent && "border-muted-foreground/30 bg-slate-900 text-muted-foreground/50",
                )}
              >
                <Icon className="size-5" />
              </div>
              <span
                className={cn(
                  "text-center text-xs font-medium",
                  isCompleted && "text-green-500",
                  isCurrent && "text-blue-500",
                  !isCompleted && !isCurrent && "text-muted-foreground/50",
                )}
              >
                {step.label}
                {step.phase === "invoice_editable" && !isCompleted && !isCurrent && (
                  <span className="block text-[10px] font-normal text-muted-foreground/50">
                    dès le {formatDateToLocale(trainingEndDate)}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
